import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '@/entities/session';
import { VerificationCta } from '@/widgets/user-verification-cta';
import { MainPageSection } from './MainPageSection';
import { AccountsCarousel, AssetsList } from '@/pages/main';
import { LastOperationsList } from './operations-history/OperationsHistory';
import { useAssetsIcons } from '@/shared/api/queries/useAssetsIconsDictionary';
import { NUMERIC_CONFIG } from '@/shared/config';
import { AssetsListEmptyState, ErrorText } from '@/shared/components';
import { useAccountCreateModal } from '@/widgets/account-create';
import { useOpenAccountAvailability } from '@/features/account-open/model';
import {
  type AccountType,
  useAccountsRests,
  useAccountsStore,
  useAccountsConfigurations,
} from '@/entities/account';
import { useAllOperationMethods } from '@/widgets';
import { useAppSettingsStore, useModalStore } from '@/shared/lib';
import { useInternalTransferModals } from '@/features/internal-transfer';
import { useApiTokensModal } from '@/widgets/api-token-list-modal';
import type { AccountSectionCardType } from '@/shared/types';
import { useVerificationInfoModal } from '@/entities/verification';
import { useOperationHistoryData } from '@/shared/lib/useOperationHistoryData.ts';
import { useAppSettingsDictionary } from '@/shared/api';

const MainPage = () => {
  const { t } = useTranslation();

  const [selectedAccountCard, setSelectedAccountCard] =
    useState<AccountSectionCardType | null>(null);

  const { getState, setState } = useModalStore();
  const { session } = useSessionStore();
  const isUserVerified = session?.additional?.isVerified || false;
  const {
    baseAsset,
    isBalancesHidden,
    setIsBalancesHidden,
    isLowBalancesHidden,
  } = useAppSettingsStore();
  const { appSettingsData } = useAppSettingsDictionary();

  const { assetsIconsData, isAssetsIconsLoading } = useAssetsIcons();

  const { openCreateAccountModal } = useAccountCreateModal();
  const { openInternalTransferFormModal } = useInternalTransferModals();
  const { depositMethods, withdrawMethods } = useAllOperationMethods();
  const { openVerificationInfoModal } = useVerificationInfoModal();

  const {
    operationsHistory,
    operationsStatusIconsData,
    operationTypeIconsData,
    isOperationsHistoryDataLoading,
  } = useOperationHistoryData('all');

  const {
    data: accountsRests,
    isLoading: isAccountsRestsLoading,
    error: accountsRestsError,
  } = useAccountsRests();
  const { accountsQty, general } = useAccountsStore();
  const { isAccountsConfigurationsLoading } = useAccountsConfigurations();

  const isLoading =
    isAccountsRestsLoading || !general || isAccountsConfigurationsLoading;

  useOpenAccountAvailability(accountsRests);

  const { openApiTokensModal } = useApiTokensModal();

  useEffect(() => {
    if (getState('openDepositOnMount')) {
      depositMethods.openOperationMethodsModal();
      setState('openDepositOnMount', false);
    }
  }, [depositMethods, getState, setState]);

  const basePrecission = useMemo(
    () =>
      accountsRests?.ewallet[0]?.basePrecission ||
      NUMERIC_CONFIG.precision.default,
    [accountsRests]
  );

  if (accountsRestsError) {
    return (
      <ErrorText>
        {accountsRestsError instanceof Error
          ? accountsRestsError.message
          : 'Server error'}
      </ErrorText>
    );
  }

  const handleOpenInternalTransfer = (context?: {
    accountType: AccountType;
    accountNumber: string;
    assetCode: string;
    direction?: 'from' | 'to';
  }) => {
    if (context) {
      openInternalTransferFormModal({
        initialContext: {
          accountType: context.accountType,
          accountNumber: context.accountNumber,
          assetCode: context.assetCode,
          direction: context.direction,
        },
      });
    } else {
      openInternalTransferFormModal();
    }
  };

  const hasOperations =
    operationsHistory?.operations && operationsHistory.operations.length > 0;

  const handleAccountCardClick = (accountCard: AccountSectionCardType) => {
    setSelectedAccountCard(prev => (prev === accountCard ? null : accountCard));
  };

  const isAccountCardSelected = (accountType: AccountType) =>
    selectedAccountCard?.type === accountType;

  const handleAccountClick = (accountNumber: string) => {
    openApiTokensModal(accountNumber);
  };

  return (
    <div className="px-[2.4rem] tablet:pl-0 tablet:pr-[4.8rem] py-[1.8rem] grow flex flex-col gap-[4.2rem] tablet:min-h-0 tablet:h-full tablet:overflow-hidden">
      {isUserVerified && (
        <MainPageSection
          title={t('personalAccount.accountsSection.title')}
          isBalancesHidden={isBalancesHidden}
          setIsBalancesHidden={setIsBalancesHidden}
          hiddenBalancesTitle={t(
            `personalAccount.accountsSection.${isBalancesHidden ? 'showBalances' : 'hideBalances'}`
          )}
          className="gap-0">
          <AccountsCarousel
            isBalancesHidden={isBalancesHidden}
            isLoading={isLoading}
            onCreateAccount={openCreateAccountModal}
            onAccountCardClick={handleAccountCardClick}
            isAccountCardSelected={isAccountCardSelected}
            onDeposit={depositMethods.openOperationMethodsModal}
            onWithdraw={withdrawMethods.openOperationMethodsModal}
            onInternalTransfer={handleOpenInternalTransfer}
            accountsRests={
              accountsRests ?? {
                ewallet: [],
                invest: null,
                spot: [],
                leverage: null,
                earn: null,
              }
            }
            onAccountClick={handleAccountClick}
          />
        </MainPageSection>
      )}
      {!isUserVerified ? (
        <VerificationCta ewalletAccNum={session?.additional?.eWallet} />
      ) : general?.balance === 0 && !hasOperations && !isLoading ? (
        <MainPageSection title={t('personalAccount.assetsSection.title')}>
          <AssetsListEmptyState
            onClick={depositMethods.openOperationMethodsModal}
          />
        </MainPageSection>
      ) : (
        <div className="flex flex-col tablet:flex-row gap-[2.4rem] w-full items-stretch tablet:flex-1 tablet:min-h-0 tablet:overflow-hidden">
          <MainPageSection
            className="w-full flex flex-col tablet:min-h-0 tablet:overflow-hidden"
            title={t(
              `personalAccount.assetsSection.title${selectedAccountCard?.type ? `-${selectedAccountCard?.type}` : ''}`
            )}
            // isBalancesHidden={isLowBalancesHidden}
            // setIsBalancesHidden={setIsLowBalancesHidden}
            // hiddenBalancesTitle={t(
            //   `personalAccount.accountsSection.${isLowBalancesHidden ? 'showLowBalances' : 'hideLowBalances'}`
            // )}
          >
            <AssetsList
              isBalancesHidden={isBalancesHidden}
              isLowBalancesHidden={isLowBalancesHidden}
              baseAccountAssets={appSettingsData?.value?.accountAssets ?? []}
              selectedAccountType={selectedAccountCard?.type ?? null}
              accountsRests={accountsRests}
              assetsIcons={assetsIconsData?.value}
              isLoading={isAssetsIconsLoading || isLoading}
              baseAsset={baseAsset}
              basePrecission={basePrecission}
              isVerified={isUserVerified}
              accountsQty={accountsQty}
              openDepositModal={depositMethods.openOperationMethodsModal}
              openWithdrawModal={withdrawMethods.openOperationMethodsModal}
              openInternalTransferFormModal={handleOpenInternalTransfer}
              openVerificationInfoModal={openVerificationInfoModal}
            />
          </MainPageSection>

          <MainPageSection
            className="w-full flex flex-col tablet:min-h-0 tablet:overflow-hidden"
            title={t(
              `personalAccount.lastOperationsSection.title${selectedAccountCard?.type ? `-${selectedAccountCard?.type}` : ''}`
            )}>
            <LastOperationsList
              isBalancesHidden={isBalancesHidden}
              operationsHistory={operationsHistory}
              operationsStatusIconsData={operationsStatusIconsData}
              operationTypeIconsData={operationTypeIconsData}
              isOperationsHistoryDataLoading={isOperationsHistoryDataLoading}
              isLoading={isLoading}
              isAccountCardSelected={Boolean(selectedAccountCard?.type)}
              selectedCardAccountNumbers={
                selectedAccountCard?.data?.accounts?.map(
                  account => account.accNum
                ) ?? []
              }
            />
          </MainPageSection>
        </div>
      )}
    </div>
  );
};

export { MainPage };
